import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { BarChart, Heart, MessageSquare, History, Brain } from "lucide-react";
import { CandidateKanban } from "@/components/candidates/CandidateKanban";
import { CandidatePotentialMatches } from "@/components/candidates/CandidatePotentialMatches";
import { CandidateComments } from "@/components/candidates/CandidateComments";
import { CandidateHistoryTab } from "@/components/candidates/CandidateHistoryTab";
import { CandidateAnalysisTab } from "@/components/candidates/CandidateAnalysisTab";
import { CandidateComment, Candidate } from "@/types/candidate";
import { useState, useEffect } from "react";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useTranslation } from "react-i18next";

interface CandidateTabsProps {
  candidateId: string;
  comments: CandidateComment[];
  cv: string | null;
  candidate: Candidate;
  handleAddComment: (content: string) => Promise<void>;
  handleUpdateComment: (commentId: string, content: string) => Promise<void>;
  handleDeleteComment: (commentId: string) => Promise<void>;
  activeTab: string;
  setActiveTab: (tab: string) => void;
}

export const CandidateTabs = ({
  candidateId,
  comments,
  cv,
  candidate,
  handleAddComment,
  handleUpdateComment,
  handleDeleteComment,
  activeTab,
  setActiveTab
}: CandidateTabsProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [potentialMatches, setPotentialMatches] = useState([]);
  const { t } = useTranslation();
  
  const findMatches = async () => {
    if (!candidateId) return;

    setIsLoading(true);
    try {
      console.log("Invoking quick-match function with candidateId:", candidateId);
      const { data, error } = await supabase.functions.invoke("quick-match", {
        body: { candidateId }
      });

      if (error) {
        console.error("Error finding matches:", error);
        throw error;
      }

      console.log("Received match results:", data);
      if (data && data.results) {

        let top10Results = Object.values(data.results).slice(0, 10);

        // Transform the results into the format expected by CandidatePotentialMatches
        const formattedMatches = Object.values(top10Results).map((result: any) => ({
          description: result.description || "No description available",
          position: result.title || "Unknown Position",
          match: Math.round(result.scorePercent * 100),
          jobId: result.id // Add job ID for navigation
        }));
        setPotentialMatches(formattedMatches);
      }
    } catch (error) {
      console.error("Error finding matches:", error);
      /* toast({
        title: "Error",
        description: "Failed to find matches. Please try again.",
        variant: "destructive",
      }); */
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === "matches" && candidateId) {
      findMatches();
    }
  }, [activeTab, candidateId]);

  console.log(candidate.ai_analysis)

  return (
    <div className="w-full">
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="w-full mb-4 flex flex-wrap gap-x-2 gap-y-2 bg-gray-100 rounded-lg p-2">
          <TabsTrigger value="processes" className="flex-shrink-0 flex items-center gap-2 min-w-max">
            <BarChart className="h-4 w-4" />
            <span>{t("candidates.tabs.recruitmentProcess")}</span>
          </TabsTrigger>
          <TabsTrigger value="matches" className="flex-shrink-0 flex items-center gap-2 min-w-max">
            <Heart className="h-4 w-4" />
            <span>{t("candidates.tabs.potentialMatches")}</span>
          </TabsTrigger>
          <TabsTrigger value="comments" className="flex-shrink-0 flex items-center gap-2 min-w-max">
            <MessageSquare className="h-4 w-4" />
            <span>{t("candidates.tabs.comments")}</span>
          </TabsTrigger>
          <TabsTrigger value="analysis" className="flex-shrink-0 flex items-center gap-2 min-w-max">
             <Brain className="h-4 w-4" />
             <span>{t("candidates.tabs.aiAnalysis")}</span>
           </TabsTrigger>
          <TabsTrigger value="history" className="flex-shrink-0 flex items-center gap-2 min-w-max">
            <History className="h-4 w-4" />
            <span>{t("candidates.tabs.history")}</span>
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="processes" className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold mb-1">{t("candidates.tabsContent.recruitmentProcess.title")}</h2>
          <p className="text-gray-500 mb-4">{t("candidates.tabsContent.recruitmentProcess.description")}</p>
          <CandidateKanban candidateId={candidateId} />
        </TabsContent>
        
        <TabsContent value="matches" className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold mb-1">{t("candidates.tabsContent.potentialMatches.title")}</h2>
          <p className="text-gray-500 mb-4">{t("candidates.tabsContent.potentialMatches.description")}</p>
          <CandidatePotentialMatches matches={potentialMatches} isLoading={isLoading} />
        </TabsContent>
        
        <TabsContent value="comments" className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold mb-1">{t("candidates.tabsContent.comments.title")}</h2>
          <p className="text-gray-500 mb-4">{t("candidates.tabsContent.comments.description")}</p>
          <CandidateComments 
            candidateId={candidateId} 
            comments={comments} 
            cv={cv}
            handleAddComment={handleAddComment}
            handleUpdateComment={handleUpdateComment}
            handleDeleteComment={handleDeleteComment}
          />
        </TabsContent>

        <TabsContent value="analysis" className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold mb-1">{t("candidates.tabsContent.aiAnalysis.title")}</h2>
          <p className="text-gray-500 mb-4">{t("candidates.tabsContent.aiAnalysis.description")}</p>
          <CandidateAnalysisTab 
            candidateId={candidateId}
            analysis={candidate.ai_analysis || {}}
          />
        </TabsContent>
        
        <TabsContent value="history" className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold mb-1">{t("candidates.tabsContent.history.title")}</h2>
          <p className="text-gray-500 mb-4">{t("candidates.tabsContent.history.description")}</p>
          <CandidateHistoryTab candidateId={candidateId} />
        </TabsContent>
      </Tabs>
    </div>
  );
};
